import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Badge, LoadingSkeleton } from '../ui';
import { safeErrorMessage } from '../../utils/apiError';

function ScatterChart({ points }) {
  const usable = points.filter((p) => p.attendance_rate !== null && p.average_grade !== null);
  if (usable.length === 0) {
    return <p style={{ color: 'var(--color-text-muted)', fontSize: '0.85rem' }}>Not enough data yet — need both attendance and grade records for at least one student.</p>;
  }

  const size = 320;
  const pad = 30;
  const maxGrade = Math.max(100, ...usable.map((p) => p.average_grade));
  const x = (rate) => pad + (rate / 100) * (size - 2 * pad);
  const y = (grade) => size - pad - (grade / maxGrade) * (size - 2 * pad);

  return (
    <svg width={size} height={size} role="img" aria-label="Attendance rate vs average grade scatter plot">
      <line x1={pad} y1={size - pad} x2={size - pad} y2={size - pad} stroke="var(--color-border)" />
      <line x1={pad} y1={pad} x2={pad} y2={size - pad} stroke="var(--color-border)" />
      <text x={size / 2} y={size - 6} textAnchor="middle" fontSize="10" fill="var(--color-text-muted)">Attendance rate (%)</text>
      <text x={10} y={size / 2} textAnchor="middle" fontSize="10" fill="var(--color-text-muted)" transform={`rotate(-90 10 ${size / 2})`}>Average grade</text>
      {usable.map((p) => (
        <circle key={p.student_id} cx={x(p.attendance_rate)} cy={y(p.average_grade)} r={5} fill="var(--color-primary)" fillOpacity={0.75}>
          <title>{`${p.student_name}: ${p.attendance_rate}% attendance, ${p.average_grade} average grade`}</title>
        </circle>
      ))}
    </svg>
  );
}

function AttendanceAnalytics({ token }) {
  const [classes, setClasses] = useState([]);
  const [terms, setTerms] = useState([]);
  const [classId, setClassId] = useState('');
  const [termId, setTermId] = useState('');

  const [punctuality, setPunctuality] = useState(null);
  const [coverage, setCoverage] = useState(null);
  const [topicCoverage, setTopicCoverage] = useState(null);
  const [correlation, setCorrelation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetch('/api/v1/classes', { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : []))
      .then(setClasses)
      .catch(() => {});
    fetch('/api/v1/terms', { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => {
        setTerms(data);
        const current = data.find((t) => t.is_current);
        if (current) setTermId(String(current.id));
      })
      .catch(() => {});
  }, [token]);

  const fetchAnalytics = useCallback(async () => {
    if (!classId || !termId) return;
    setLoading(true);
    setError('');
    try {
      const params = `class_id=${classId}&term_id=${termId}`;
      const [pRes, cRes, tRes, gRes] = await Promise.all([
        fetch(`/api/v1/attendance/analytics/punctuality?${params}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`/api/v1/attendance/analytics/coverage?${params}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`/api/v1/attendance/analytics/topic-coverage?${params}`, { headers: { Authorization: `Bearer ${token}` } }),
        fetch(`/api/v1/attendance/analytics/attendance-vs-grades?${params}`, { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      if (pRes.ok) setPunctuality(await pRes.json()); else setError(await safeErrorMessage(pRes, 'Failed to load punctuality data'));
      if (cRes.ok) setCoverage(await cRes.json());
      if (tRes.ok) setTopicCoverage(await tRes.json());
      if (gRes.ok) setCorrelation(await gRes.json());
    } catch {
      setError('Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  }, [token, classId, termId]);

  useEffect(() => { fetchAnalytics(); }, [fetchAnalytics]);

  const punctualityColumns = [
    { header: 'Student', accessor: 'student_name' },
    { header: 'Present', render: (r) => `${r.present_rate}%` },
    { header: 'Late', render: (r) => `${r.late_rate}%` },
    { header: 'Absent', render: (r) => `${r.absent_rate}%` },
    { header: 'Excused', render: (r) => `${r.excused_rate}%` },
    { header: 'Sessions', accessor: 'total_sessions' },
  ];

  const coverageColumns = [
    { header: 'Subject', accessor: 'subject' },
    { header: 'Day', accessor: 'day_of_week' },
    { header: 'Expected', accessor: 'expected_count' },
    { header: 'Delivered', accessor: 'delivered_count' },
    { header: 'Delivery Rate', render: (r) => <Badge variant={r.delivery_rate >= 80 ? 'success' : r.delivery_rate >= 50 ? 'warning' : 'danger'}>{r.delivery_rate}%</Badge> },
  ];

  const topicColumns = [
    { header: 'Subject', accessor: 'subject_name' },
    { header: 'Sessions', accessor: 'total_sessions' },
    { header: 'With Topic Logged', accessor: 'sessions_with_topic' },
    { header: 'Coverage', render: (r) => <Badge variant={r.coverage_pct >= 80 ? 'success' : r.coverage_pct >= 50 ? 'warning' : 'danger'}>{r.coverage_pct}%</Badge> },
  ];

  return (
    <div>
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
        <div className="form-group" style={{ maxWidth: '240px' }}>
          <label>Class</label>
          <select value={classId} onChange={(e) => setClassId(e.target.value)}>
            <option value="">Select a class</option>
            {classes.map((c) => <option key={c.id} value={c.id}>{c.display_name}</option>)}
          </select>
        </div>
        <div className="form-group" style={{ maxWidth: '240px' }}>
          <label>Term</label>
          <select value={termId} onChange={(e) => setTermId(e.target.value)}>
            <option value="">Select a term</option>
            {terms.map((t) => <option key={t.id} value={t.id}>{t.name || `Term ${t.term_number} ${t.year}`}</option>)}
          </select>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {!classId || !termId ? (
        <p style={{ color: 'var(--color-text-muted)' }}>Select a class and term to see analytics.</p>
      ) : loading ? (
        <LoadingSkeleton type="text" rows={8} />
      ) : (
        <>
          <Card title="Punctuality">
            <Table columns={punctualityColumns} data={punctuality?.results || []} emptyMessage="No submitted attendance for this class/term yet." />
          </Card>

          <Card title="Periods Scheduled vs Delivered">
            {coverage?.caveat && (
              <p style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', marginBottom: '0.75rem' }}>{coverage.caveat}</p>
            )}
            <Table columns={coverageColumns} data={coverage?.periods || []} emptyMessage="No subject-period timetable slots for this class/term." />
          </Card>

          <Card title="Topic Coverage">
            {topicCoverage?.note && (
              <p style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', marginBottom: '0.75rem' }}>{topicCoverage.note}</p>
            )}
            <Table columns={topicColumns} data={topicCoverage?.results || []} emptyMessage="No submitted subject-period registers for this class/term." />
          </Card>

          <Card title="Attendance vs. Grades">
            <ScatterChart points={correlation?.points || []} />
          </Card>
        </>
      )}
    </div>
  );
}

export default AttendanceAnalytics;
